import React, { useMemo, PropsWithChildren } from 'react';
import { RemoteModule } from '@invent/platform-api';
import {
  usePlatformWidgetSelector,
  usePublicWidgetsLoadedSelector,
  usePortalWidgetSettingsSelector,
  usePortalSettingsSelector
} from '@/src/domains/widgets/selectors';
import { useAuth } from '@/src/domains/auth/hooks';
import { useWidgetDataGenerator } from '@/lib/module-federation';
import { useDashboardsPostMutation } from '@/src/domains/dashboards/queries';

export interface IPlatformWidgetProps {
  domain: string;
  placement: string;
  fallback: IRemoteModuleFallback;
  componentProps?: Record<string, unknown>;
}

export const PlatformWidget = ({
  domain,
  placement,
  fallback,
  componentProps = {},
  children
}: PropsWithChildren<IPlatformWidgetProps>) => {
  const { signinRedirect, signout } = useAuth();
  const widgetMeta = usePlatformWidgetSelector(domain, placement);
  const isPublicWidgetsLoaded = usePublicWidgetsLoadedSelector();
  const widgetDataGenerator = useWidgetDataGenerator();
  const { flatNavGraph } = usePortalSettingsSelector();
  const portalSettingsProps = usePortalWidgetSettingsSelector(widgetMeta?.widgetId, placement);

  const moduleData = useMemo(
    () => widgetMeta && widgetDataGenerator(widgetMeta.name, widgetMeta.widgetId),
    [widgetMeta, widgetDataGenerator]
  );

  if (!moduleData) {
    if (isPublicWidgetsLoaded) {
      const DefaultComponent = fallback.default as React.ComponentType<PropsWithChildren<any>>;

      return <DefaultComponent {...componentProps}>{children}</DefaultComponent>;
    }

    const Loading = fallback.isLoading;

    return <Loading />;
  }

  return (
    <RemoteModule
      scope={moduleData.scope}
      url={moduleData.host}
      module={moduleData.component}
      fallback={fallback}
      componentProps={{
        children,
        handleSignin: signinRedirect,
        handleSignout: signout,
        useDashboardsPostMutation,
        flatNavGraph,
        ...componentProps,
        ...widgetMeta?.defaultProps,
        ...portalSettingsProps
      }}
    />
  );
};
